import { getAssignedProviderName } from '@brands/Dashboard/ConsultationPage/utils/complianceChecklistCopy';
import { isAlmostEqualWithTolerance } from '@brands/Dashboard/MyProfile/Patient/utils/utilsFunctions';
import { getPainReasonForVisitText } from '@brands/Dashboard/PatientForms/PatientForm/DescribePainLevel/DescribePainLevel';
import { config } from '@brands/config/config';
import { CaseType, ICase } from '@brands/services/cases/types/ICase';
import { MedicalConditions, MedicalHistory } from '@brands/services/clinical_data/types/types';
import { ImageWithNotes } from '@brands/services/forms/types/ImageWithNotes';
import { ICDT } from '@brands/services/oneReport/types/IOneReport';
import { IPrescription, ITreatments, Pharmacy } from '@brands/services/oneReport/types/ITreatments';
import { normalizeAdditionalInfo } from '@brands/Utils/additionalInfo';
import { getFriendlyName, getPainTypeText, getUserRoleName } from '@brands/Utils/getFriendlyName';
import { mapPainLevelLabel } from '@brands/Utils/mapPainLevelLabel';
import {
  careCoordinationUrgencyOptions,
  careCoordinationTypeOptions,
  oneReportTreatmentsCategory,
  oneReportTreatmentsToothNumber,
  oneReportTreatmentsUrgency,
  oralHealthCoachingTypeOptions,
} from '@brands/Utils/selectOptions';
import { parsePhoneNumber } from 'libphonenumber-js';
import moment from 'moment-timezone';

export type ReportField = {
  label: string;
  value: string;
};

export type ReportRichText = {
  html: string;
  plainText: string;
};

export type ReportAdditionalInfo = {
  id: string;
  date?: string;
  text: string;
};

export type ReportPrescription = {
  id: string;
  drug: string;
  instructions: string;
  prescriptionDate: string;
  dispense: string;
  refills: string;
  pharmacy: ReportField[];
};

export type ReportImage = ImageWithNotes & {
  id: string;
  displayDate?: string;
};

export type ReportFollowUpColumn = {
  id: 'care_coordination' | 'oral_health_coaching';
  recommendedLabel: string;
  recommendedValue: string;
  fields: ReportField[];
};

export type ReportEducation = {
  id: string;
  title: string;
  url: string;
};

export type ReportTreatment = {
  id: string;
  fields: ReportField[];
  description: string;
};

export type ReportFulfillmentApproval = {
  decision: string;
  notes: string;
};

export type ReportNote = {
  id: string;
  meta: string;
  html: string;
};

export type ReportProviderAttestation = {
  date: string;
  providerName: string;
};

export type OHRReportViewModel = {
  headerDate: string;
  appointmentLeft: ReportField[];
  appointmentRight: ReportField[];
  careTeamLeft: ReportField[];
  careTeamRight: ReportField[];
  careTeamCollector: ReportField[];
  patientLeft: ReportField[];
  patientRight: ReportField[];
  visitSummaryLeft: ReportField[];
  additionalInfo: ReportAdditionalInfo[];
  providerComment: ReportRichText | null;
  assessment: ReportRichText | null;
  cdtCodes: ICDT[];
  prescriptions: ReportPrescription[];
  images: ReportImage[];
  recommendedTreatment: ReportRichText | null;
  treatments: ReportTreatment[];
  fulfillmentApproval: ReportFulfillmentApproval | null;
  followUpColumns: ReportFollowUpColumn[];
  educations: ReportEducation[];
  notes: ReportNote[];
  providerAttestation: ReportProviderAttestation | null;
};

export const OHR_LEGAL_DISCLAIMER =
  'This platform connects users with dental providers, but does not provide dental care or services directly. ' +
  'Users establish relationships with individual providers, who are solely responsible for the advice and treatment ' +
  'they provide. The platform does not control the advice given by providers, and users are responsible for their ' +
  "own dental health data and for following their provider's recommendations.";

const hiddenMedicalConditionKeys = new Set(['id', 'user_id', 'created_at', 'updated_at']);

const conditionLabels: Record<string, string> = {
  acid_reflux: 'Acid reflux',
  aids_hiv: 'AIDS/HIV',
  alzheimers_disease: "Alzheimer's disease",
  artificial_joints: 'Artificial joints',
  cancer_chemo_radiation: 'Cancer, chemo, radiation',
  copd: 'COPD',
  dementia_alzheimers: "Dementia/Alzheimer's",
  dry_mouth: 'Dry mouth (xerostomia)',
  hepatitis_a: 'Hepatitis A',
  hepatitis_b: 'Hepatitis B',
  hepatitis_c: 'Hepatitis C',
  heart_pacemaker: 'Heart pacemaker',
  high_blood_pressure: 'High blood pressure',
  low_blood_pressure: 'Low blood pressure',
  mitral_valve_prolapse: 'Mitral valve prolapse',
  pregnancy_or_breastfeeding: 'Pregnancy or breastfeeding',
  respiratory_conditions: 'Respiratory conditions',
  sexually_transmitted_disease: 'Sexually transmitted disease',
  sickle_cell_disease: 'Sickle cell disease',
};

export const isSynchronousCase = (caseType?: string): boolean =>
  caseType === CaseType.video_call_scheduled || caseType === CaseType.phone_call_scheduled;

export const stripHtml = (html?: string | null): string =>
  (html || '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();

export const hasMeaningfulText = (value?: string | number | null): boolean => {
  if (value === null || value === undefined) return false;

  const normalized = String(value)
    .replace(/&nbsp;/g, ' ')
    .trim();

  return normalized.length > 0;
};

const hasMeaningfulOptionalAnswer = (value?: string | number | null): boolean => {
  if (!hasMeaningfulText(value)) return false;

  return String(value).trim().toLowerCase() !== 'no';
};

export const hasMeaningfulHtml = (html?: string | null): boolean => hasMeaningfulText(stripHtml(html));

export const displayPhoneNumber = (phoneNumber?: string | null): string => {
  if (!hasMeaningfulOptionalAnswer(phoneNumber)) return '';

  try {
    return parsePhoneNumber(phoneNumber || '', 'US')?.formatNational() || '';
  } catch {
    return phoneNumber || '';
  }
};

export const formatReportDate = (dateString?: string | null, timeZone = moment.tz.guess()): string => {
  const date = dateString ? moment.utc(dateString).tz(timeZone) : moment().tz(timeZone);
  return date.isValid() ? date.format('MMMM D, YYYY') : '';
};

export const formatReportDateTime = (dateString?: string | null, timeZone = moment.tz.guess()): string => {
  if (!dateString) return '';

  const date = moment.utc(dateString).tz(timeZone);
  return date.isValid() ? date.format('MMMM D, YYYY hh:mma') : '';
};

export const formatCompactDateTime = (dateString?: string | null, timeZone = moment.tz.guess()): string => {
  if (!dateString) return '';

  const date = moment.utc(dateString).tz(timeZone);
  return date.isValid() ? date.format('MM/DD/YYYY hh:mm A') : '';
};

const toTitle = (value: string): string =>
  value.replace(/_/g, ' ').replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

const normalizeFieldValue = (value?: string | null): string =>
  hasMeaningfulOptionalAnswer(value) ? String(value).trim() : '';

const getConditionText = (medicalCondition?: MedicalConditions | null): string => {
  if (!medicalCondition) return '';

  return Object.entries(medicalCondition)
    .filter(([key]) => !hiddenMedicalConditionKeys.has(key))
    .map(([key, value]) => {
      if (key === 'conditions_not_listed') {
        return hasMeaningfulOptionalAnswer(value as string) ? String(value).trim() : null;
      }

      if (key === 'medical_condition_none_of_the_above' || key === 'none_of_the_above') {
        return null;
      }

      return value === true ? conditionLabels[key] || toTitle(key) : null;
    })
    .filter((value): value is string => Boolean(value))
    .join(', ');
};

const addressFromParts = (parts: Array<string | null | undefined>): string =>
  parts.filter((part) => hasMeaningfulOptionalAnswer(part)).join(', ');

const buildPharmacyFields = (pharmacy?: Partial<Pharmacy> | null): ReportField[] => {
  if (!pharmacy) return [];

  return [
    { label: 'Pharmacy', value: normalizeFieldValue(pharmacy.pharmacy_name) },
    {
      label: 'Location',
      value: addressFromParts([
        pharmacy.pharmacy_street_address,
        pharmacy.pharmacy_city,
        pharmacy.pharmacy_state,
        pharmacy.pharmacy_zip_code,
      ]),
    },
    { label: 'Phone number', value: displayPhoneNumber(pharmacy.pharmacy_phone_number) },
  ].filter((field) => hasMeaningfulText(field.value));
};

const richTextOrNull = (html?: string | null): ReportRichText | null =>
  hasMeaningfulHtml(html)
    ? {
        html: html || '',
        plainText: stripHtml(html),
      }
    : null;

const getFollowUpTypeLabel = (type: string, kind: 'care' | 'oral'): string => {
  if (!hasMeaningfulText(type)) return '';

  const options = kind === 'care' ? careCoordinationTypeOptions : oralHealthCoachingTypeOptions;
  return options.find((option) => option.value === type)?.label || type;
};

const getUrgencyLabel = (urgency: string): string =>
  careCoordinationUrgencyOptions.find((option) => option.value === urgency)?.label || urgency || '';

const getTreatmentLabel = (value: string, options: Array<{ value: string; label: string }>): string =>
  options.find((option) => option.value === value)?.label || value;

const hasAnsweredFollowUp = (recommended?: boolean | null, type?: string): boolean =>
  recommended === true || (recommended === false && type !== 'DO-2870');

const buildFollowUpColumn = (
  id: ReportFollowUpColumn['id'],
  recommended: boolean | null | undefined,
  type: string,
  urgency: string,
  reason: string,
): ReportFollowUpColumn | null => {
  if (!hasAnsweredFollowUp(recommended, type)) return null;

  const kind = id === 'care_coordination' ? 'care' : 'oral';

  return {
    id,
    recommendedLabel: id === 'care_coordination' ? 'Care coordination recommended' : 'Oral health coaching recommended',
    recommendedValue: recommended ? 'Yes' : 'No',
    fields: [
      { label: 'Type', value: getFollowUpTypeLabel(type, kind) },
      { label: 'Urgency', value: getUrgencyLabel(urgency) },
      { label: 'Reason', value: reason },
    ].filter((field) => hasMeaningfulText(field.value)),
  };
};

type BuildOHRReportViewModelArgs = {
  thisCase: ICase;
  images: ImageWithNotes[];
  medicalHistory?: MedicalHistory | null;
  medicalCondition?: MedicalConditions | null;
  pharmacyAddress?: string;
  pharmacyPhoneNumber?: string;
  timeZone?: string;
};

export const buildOHRReportViewModel = ({
  thisCase,
  images,
  medicalHistory,
  medicalCondition,
  pharmacyAddress,
  pharmacyPhoneNumber,
  timeZone = moment.tz.guess(),
}: BuildOHRReportViewModelArgs): OHRReportViewModel => {
  const synchronousCase = isSynchronousCase(thisCase.type);
  const reportCompletedAt = thisCase.completed_at || thisCase.one_report?.completed_at || thisCase.updated_at;
  // Report insurance only when insurance was used for this case.
  const insuranceCarrier = thisCase.insurance?.id
    ? thisCase.insurance.insurance_membership?.insurance_carrier?.name || ''
    : '';
  const conditions = getConditionText(medicalCondition);
  const medications = normalizeFieldValue(medicalHistory?.medications);
  const allergies = normalizeFieldValue(medicalHistory?.medications_allergies);
  const patientPhone = displayPhoneNumber(thisCase.patient?.phone_number);
  const selectedImages = (images || []).filter((image) => image.isSelected);
  const painLevel = thisCase.case_intake_questions?.pain_level || '';
  const hasPain = hasMeaningfulText(painLevel) && painLevel !== 'noPain';
  const followUps = thisCase.one_report?.follow_ups;
  const fulfillmentApproval =
    config.features.multiStage && thisCase.one_report?.fulfillment_approval
      ? {
          decision: thisCase.one_report.fulfillment_approval.approved ? 'Approved' : 'Not approved',
          notes: normalizeFieldValue(thisCase.one_report.fulfillment_approval.notes),
        }
      : null;

  const careFollowUp = buildFollowUpColumn(
    'care_coordination',
    followUps?.care_coordination,
    followUps?.care_coordination_type || '',
    followUps?.care_coordination_urgency || '',
    followUps?.care_coordination_reason || '',
  );
  const coachingFollowUp = buildFollowUpColumn(
    'oral_health_coaching',
    followUps?.oral_health_coaching,
    followUps?.oral_health_coaching_type || '',
    followUps?.oral_health_coaching_urgency || '',
    followUps?.oral_health_coaching_reason || '',
  );

  const prescriptions = (thisCase.one_report?.prescriptions || [])
    .filter((prescription) => hasMeaningfulText(prescription.drug) || hasMeaningfulText(prescription.sig))
    .map((prescription: IPrescription, index) => ({
      id: prescription.id || String(index),
      drug: normalizeFieldValue(prescription.drug),
      instructions: normalizeFieldValue(prescription.sig),
      prescriptionDate: normalizeFieldValue(prescription.prescription_date),
      dispense: normalizeFieldValue(prescription.dispense),
      refills: normalizeFieldValue(prescription.refills),
      pharmacy: buildPharmacyFields(prescription.pharmacy),
    }));

  const treatments = (thisCase.one_report?.treatments || [])
    .filter(
      (treatment) =>
        hasMeaningfulText(treatment.category) ||
        hasMeaningfulText(treatment.tooth_number) ||
        hasMeaningfulText(treatment.tooth_area) ||
        hasMeaningfulText(treatment.urgency) ||
        hasMeaningfulText(treatment.description),
    )
    .map((treatment: ITreatments, index) => ({
      id: treatment.id || String(index),
      fields: [
        {
          label: 'Category',
          value: hasMeaningfulText(treatment.category)
            ? getTreatmentLabel(treatment.category, oneReportTreatmentsCategory)
            : '',
        },
        {
          label: 'Tooth # / Area',
          value:
            getTreatmentLabel(treatment.tooth_number || '', oneReportTreatmentsToothNumber) ||
            getTreatmentLabel(treatment.tooth_area || '', oneReportTreatmentsToothNumber),
        },
        {
          label: 'Urgency',
          value: hasMeaningfulText(treatment.urgency)
            ? getTreatmentLabel(treatment.urgency, oneReportTreatmentsUrgency)
            : '',
        },
      ].filter((field) => hasMeaningfulText(field.value)),
      description: normalizeFieldValue(treatment.description),
    }));

  return {
    headerDate: formatReportDate(reportCompletedAt, timeZone),
    appointmentLeft: [
      synchronousCase
        ? {
            label: 'Video consultation',
            value:
              formatReportDateTime(thisCase.video_conference_date, timeZone) ||
              formatReportDateTime(thisCase.created_at, timeZone),
          }
        : { label: 'Service requested', value: formatReportDateTime(thisCase.created_at, timeZone) },
      synchronousCase
        ? { label: 'Service requested', value: formatReportDateTime(thisCase.created_at, timeZone) }
        : { label: 'Report completed', value: formatReportDateTime(reportCompletedAt, timeZone) },
    ].filter((field) => hasMeaningfulText(field.value)),
    appointmentRight: [
      { label: 'Consult type', value: getFriendlyName(thisCase.type) },
      ...(synchronousCase
        ? [{ label: 'Report completed', value: formatReportDateTime(reportCompletedAt, timeZone) }]
        : []),
    ].filter((field) => hasMeaningfulText(field.value)),
    careTeamLeft: [
      {
        label: 'Provider',
        value: [thisCase.provider?.full_name || '', thisCase.provider?.provider_profile?.credentials || '']
          .filter(Boolean)
          .join(', '),
      },
      {
        label: 'License #',
        value:
          thisCase.provider?.provider_profile?.licenses?.find(
            (license) => license.state === thisCase.patient_current_state,
          )?.number || '',
      },
    ].filter((field) => hasMeaningfulText(field.value)),
    careTeamRight: [
      ...(thisCase.care_advisor?.full_name ? [{ label: 'Care Advisor', value: thisCase.care_advisor.full_name }] : []),
      {
        label: 'Provider NPI #',
        value: thisCase.provider?.provider_profile?.national_provider_id || '',
      },
    ].filter((field) => hasMeaningfulText(field.value)),
    careTeamCollector: [{ label: 'Collector', value: thisCase.collector?.full_name || '' }].filter((field) =>
      hasMeaningfulText(field.value),
    ),
    patientLeft: [
      { label: 'Patient name', value: thisCase.patient?.full_name || '' },
      { label: 'EID', value: thisCase.patient?.external_id || '' },
      {
        label: 'DOB',
        value: thisCase.patient?.basic_info?.date_of_birth
          ? moment(thisCase.patient.basic_info.date_of_birth).format('MM/DD/YYYY')
          : '',
      },
      { label: 'Insurance', value: insuranceCarrier },
      { label: 'Email', value: thisCase.patient?.email || '' },
      { label: 'Phone number', value: patientPhone },
    ].filter((field) => hasMeaningfulText(field.value)),
    patientRight: [
      { label: 'Conditions', value: conditions },
      { label: 'Medications', value: medications },
      { label: 'Allergies', value: allergies },
      { label: 'Pharmacy', value: normalizeFieldValue(medicalHistory?.pharmacy_name) },
      { label: 'Location', value: normalizeFieldValue(pharmacyAddress) },
      { label: 'Phone number', value: normalizeFieldValue(pharmacyPhoneNumber) },
    ].filter((field) => hasMeaningfulText(field.value)),
    visitSummaryLeft:
      thisCase.type === CaseType.clinical_enablement
        ? []
        : [
            { label: 'Emergency case', value: thisCase.case_intake_questions?.is_dental_emergency ? 'Yes' : 'No' },
            ...(hasPain
              ? [
                  {
                    label: 'Pain type',
                    value:
                      thisCase.case_intake_questions?.pain_description
                        ?.map((pain) => getPainTypeText(pain))
                        .join(', ') || '',
                  },
                  { label: 'Pain level', value: mapPainLevelLabel(painLevel) },
                  { label: 'Time in pain', value: thisCase.case_intake_questions?.pain_time || '' },
                  {
                    label: 'Symptoms',
                    value:
                      thisCase.case_intake_questions?.reason_for_visit
                        ?.map((pain) =>
                          getPainReasonForVisitText(pain, thisCase.case_intake_questions?.is_dental_emergency),
                        )
                        .join(', ') || '',
                  },
                ]
              : []),
          ].filter((field) => hasMeaningfulText(field.value)),
    additionalInfo: (thisCase.case_intake_questions?.additional_info || [])
      .filter((info) => Boolean(normalizeAdditionalInfo(info.additional_info)))
      .map((info, index) => {
        const createdAt = info.created_at || '';
        const showDate = Boolean(createdAt && !isAlmostEqualWithTolerance(createdAt, thisCase.created_at));

        return {
          id: createdAt || String(index),
          date: showDate ? formatCompactDateTime(createdAt, timeZone) : undefined,
          text: normalizeAdditionalInfo(info.additional_info) || '',
        };
      }),
    providerComment: richTextOrNull(thisCase.one_report?.medical_history),
    assessment: richTextOrNull(thisCase.one_report?.assessments),
    cdtCodes: thisCase.one_report?.cdts || [],
    prescriptions,
    images: selectedImages.map((image, index) => ({
      ...image,
      id: image.created_at || image.src || String(index),
      displayDate: image.created_at ? formatCompactDateTime(image.created_at, timeZone) : undefined,
    })),
    recommendedTreatment: richTextOrNull(thisCase.one_report?.disposition),
    treatments,
    fulfillmentApproval,
    followUpColumns: [careFollowUp, coachingFollowUp].filter((column): column is ReportFollowUpColumn =>
      Boolean(column),
    ),
    educations: (thisCase.one_report?.educations || [])
      .filter((education) => hasMeaningfulText(education.title) || hasMeaningfulText(education.url))
      .sort((a, b) => Number(a.id) - Number(b.id))
      .map((education) => ({
        id: String(education.id),
        title: education.title,
        url: education.url,
      })),
    notes: (thisCase.one_report?.notes || [])
      .filter((note) => !note.deleted_at && hasMeaningfulHtml(note.note))
      .sort((a, b) => Number(a.id) - Number(b.id))
      .map((note) => ({
        id: String(note.id),
        meta: [
          formatCompactDateTime(note.last_edited_at || note.created_at, timeZone),
          `${note.created_by.full_name} (${getUserRoleName(note.created_by.role.name)})`,
        ]
          .filter(Boolean)
          .join('  |  '),
        html: note.note,
      })),
    providerAttestation: thisCase.completed_at
      ? {
          date: formatReportDateTime(thisCase.completed_at, timeZone),
          providerName: getAssignedProviderName(thisCase.provider?.full_name),
        }
      : null,
  };
};
